import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Container } from '../components/ui'
import { acceptInvite, validateInviteToken } from '../services/invite'
import { usePageTitle } from '../hooks/usePageTitle'

const inputClass =
  'w-full border border-rule rounded-sm bg-paper px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-300 focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink/10'
const labelClass =
  'block text-xs uppercase tracking-wide text-ink-500 font-medium mb-0.5'

const InvitePage: React.FC = () => {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState<{ email: string; name: string; purpose: string } | null>(
    null
  )
  const [loadError, setLoadError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('Manglende token i linket.')
      setLoading(false)
      return
    }
    let cancelled = false
    validateInviteToken(token)
      .then((data) => {
        if (!cancelled) setInfo(data)
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (password.length < 8) {
      setSubmitError('Adgangskode skal være mindst 8 tegn.')
      return
    }
    if (password !== confirm) {
      setSubmitError('Adgangskoderne matcher ikke.')
      return
    }
    setSubmitting(true)
    try {
      await acceptInvite(token, password)
      setDone(true)
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Noget gik galt')
    } finally {
      setSubmitting(false)
    }
  }

  const title =
    info?.purpose === 'reset' ? 'Vælg ny adgangskode' : 'Opret adgangskode'

  usePageTitle(loading ? 'Invitation' : loadError ? 'Invitation' : title)

  return (
    <Container width="prose" className="py-8">
      <p className="font-mono text-xs uppercase tracking-widest text-signal mb-1.5">
        Invitation
      </p>
      <h1 className="font-serif text-2xl text-ink mb-4">{title}</h1>

      {loading && <p className="text-sm text-ink-500">Validerer link…</p>}

      {!loading && loadError && (
        <p className="text-sm text-signal" role="alert">
          {loadError}
        </p>
      )}

      {!loading && !loadError && done && (
        <div className="space-y-3">
          <p className="text-sm text-ink">Adgangskoden er gemt. Du kan nu logge ind.</p>
          <Link
            to="/login"
            className="inline-block text-sm font-medium text-signal hover:underline"
          >
            Gå til login
          </Link>
        </div>
      )}

      {!loading && !loadError && info && !done && (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-sm">
          <p className="text-sm text-ink-500">
            Konto: <span className="text-ink font-medium">{info.name}</span> (
            {info.email})
          </p>
          <div>
            <label htmlFor="invite-password" className={labelClass}>
              Adgangskode
            </label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>
          <div>
            <label htmlFor="invite-confirm" className={labelClass}>
              Gentag adgangskode
            </label>
            <input
              id="invite-confirm"
              type="password"
              autoComplete="new-password"
              className={inputClass}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
          </div>
          {submitError && (
            <p className="text-sm text-signal" role="alert">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 bg-ink text-paper text-sm font-medium rounded-sm disabled:opacity-50"
          >
            {submitting ? 'Gemmer…' : 'Gem adgangskode'}
          </button>
        </form>
      )}
    </Container>
  )
}

export default InvitePage
